import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, authFetch } from "../App";
import { useIsDesktop } from "../components/Layout";
import ProjectTopNav from "../components/ProjectTopNav";
import { toast } from "sonner";
import { format, parseISO, isPast, isToday } from "date-fns";
import { de } from "date-fns/locale";
import {
  ListTodo,
  Plus,
  Filter,
  Search,
  ArrowLeft,
  Clock,
  Calendar,
  User,
  CheckCircle,
  Circle,
  AlertTriangle,
  Pause,
  MoreVertical,
  Trash2,
  Edit,
  Target,
  CheckSquare,
  Square,
  FileText,
  Camera,
  Image,
  X,
  Upload,
  ZoomIn,
  MapPin,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import TaskMarkerPicker from "../components/TaskMarkerPicker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const API = '/api';

// Status configuration
const STATUS_CONFIG = {
  offen: { label: "Offen", icon: Circle, color: "bg-gray-100 text-gray-700", order: 1 },
  in_arbeit: { label: "In Arbeit", icon: Clock, color: "bg-blue-100 text-blue-700", order: 2 },
  erledigt: { label: "Erledigt", icon: CheckCircle, color: "bg-green-100 text-green-700", order: 4 },
  blockiert: { label: "Blockiert", icon: Pause, color: "bg-red-100 text-red-700", order: 3 },
};

// Priority configuration
const PRIORITY_CONFIG = {
  niedrig: { label: "Niedrig", color: "bg-gray-100 text-gray-600", order: 4 },
  normal: { label: "Normal", color: "bg-blue-100 text-blue-600", order: 3 },
  hoch: { label: "Hoch", color: "bg-orange-100 text-orange-600", order: 2 },
  kritisch: { label: "Kritisch", color: "bg-red-100 text-red-600", order: 1 },
};

const MAX_PHOTOS = 20;

// ============ PHOTO GALLERY COMPONENT ============
function PhotoGallery({ photos = [], onDelete, onPreview, isEditable = false }) {
  if (photos.length === 0) return null;
  
  return (
    <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
      {photos.map((photo) => (
        <div 
          key={photo.id} 
          className="relative group aspect-square rounded-lg overflow-hidden bg-gray-100 border"
        >
          <img
            src={photo.url}
            alt={photo.filename}
            className="w-full h-full object-cover cursor-pointer"
            onClick={() => onPreview(photo)}
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
            <button
              onClick={() => onPreview(photo)}
              className="p-1.5 bg-white/90 rounded-full opacity-0 group-hover:opacity-100 transition-opacity mr-1"
            >
              <ZoomIn className="w-4 h-4 text-gray-700" />
            </button>
            {isEditable && (
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(photo); }}
                className="p-1.5 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ PHOTO PREVIEW MODAL ============
function PhotoPreviewModal({ photo, onClose }) {
  if (!photo) return null;
  
  return (
    <Dialog open={!!photo} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden">
        <div className="relative">
          <img
            src={photo.url}
            alt={photo.filename}
            className="w-full max-h-[80vh] object-contain"
          />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white px-4 py-2 text-sm">
            {photo.filename}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============ MOBILE TASK CARD ============
function MobileTaskCard({ task, onEdit, onDelete, onStatusChange, onAddToReport }) {
  const statusConfig = STATUS_CONFIG[task.status] || STATUS_CONFIG.offen;
  const priorityConfig = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.normal;
  const StatusIcon = statusConfig.icon;
  
  const isOverdue = task.due_date && isPast(parseISO(task.due_date)) && task.status !== "erledigt";
  const isDueToday = task.due_date && isToday(parseISO(task.due_date));
  
  const completedChecklist = task.checklist?.filter(c => c.checked).length || 0;
  const totalChecklist = task.checklist?.length || 0;
  const photoCount = task.photo_count || task.photos?.length || 0;

  // Quick status cycle: offen -> in_arbeit -> erledigt -> offen
  const cycleStatus = () => {
    const statusOrder = ["offen", "in_arbeit", "erledigt"];
    const currentIndex = statusOrder.indexOf(task.status);
    const nextStatus = statusOrder[(currentIndex + 1) % statusOrder.length];
    onStatusChange(task, nextStatus);
  };

  return (
    <div 
      className={`bg-white border rounded-xl p-4 mb-3 shadow-sm active:scale-[0.98] transition-transform ${
        task.status === "erledigt" ? "opacity-60" : ""
      } ${isOverdue ? "border-l-4 border-l-red-500" : ""}`}
      data-testid={`mobile-task-${task.id}`}
    >
      {/* Top Row: Checkbox + Title + Menu */}
      <div className="flex items-start gap-3">
        {/* Large Touch Target for Status Toggle */}
        <button
          onClick={cycleStatus}
          className={`p-2 -m-2 rounded-xl transition-colors touch-manipulation ${
            task.status === "erledigt" 
              ? "text-green-600" 
              : task.status === "in_arbeit"
                ? "text-blue-600"
                : "text-gray-400"
          }`}
          data-testid={`mobile-task-status-${task.id}`}
        >
          {task.status === "erledigt" ? (
            <CheckCircle className="w-7 h-7" />
          ) : task.status === "in_arbeit" ? (
            <Clock className="w-7 h-7" />
          ) : (
            <Circle className="w-7 h-7" />
          )}
        </button>
        
        {/* Title & Meta - Clickable for edit */}
        <div className="flex-1 min-w-0" onClick={() => onEdit(task)}>
          {/* Task Number */}
          {task.task_number && (
            <span className="text-xs font-mono text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
              {task.task_number}
            </span>
          )}
          <h3 className={`font-semibold text-gray-900 mt-1 ${
            task.status === "erledigt" ? "line-through text-gray-400" : ""
          }`}>
            {task.title}
          </h3>
          
          {task.description && (
            <p className="text-sm text-gray-500 line-clamp-2 mt-1">{task.description}</p>
          )}
        </div>
        
        {/* Actions Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-2 -m-1 rounded-lg hover:bg-gray-100 touch-manipulation">
              <MoreVertical className="w-5 h-5 text-gray-400" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => onEdit(task)} className="py-3">
              <Edit className="w-4 h-4 mr-3" />
              Bearbeiten
            </DropdownMenuItem>
            {task.status === "erledigt" && (
              <DropdownMenuItem onClick={() => onAddToReport(task)} className="text-blue-600 py-3">
                <FileText className="w-4 h-4 mr-3" />
                In Bautagesbericht
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => onDelete(task)} className="text-red-600 py-3">
              <Trash2 className="w-4 h-4 mr-3" />
              Löschen
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      {/* Checklist Progress (if exists) */}
      {totalChecklist > 0 && (
        <div className="mt-3 flex items-center gap-2">
          <CheckSquare className="w-4 h-4 text-gray-400" />
          <div className="flex-1 bg-gray-100 rounded-full h-2">
            <div 
              className="bg-green-500 h-2 rounded-full transition-all"
              style={{ width: `${(completedChecklist / totalChecklist) * 100}%` }}
            />
          </div>
          <span className="text-xs text-gray-500">{completedChecklist}/{totalChecklist}</span>
        </div>
      )}
      
      {/* Bottom Row: Tags */}
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {/* Status Badge - Tappable */}
        <button onClick={cycleStatus} className="touch-manipulation">
          <Badge className={`${statusConfig.color} gap-1 px-3 py-1`}>
            <StatusIcon className="w-3 h-3" />
            {statusConfig.label}
          </Badge>
        </button>
        
        {/* Priority Badge */}
        <Badge className={`${priorityConfig.color} px-3 py-1`}>
          {priorityConfig.label}
        </Badge>
        
        {/* Photo Count */}
        {photoCount > 0 && (
          <Badge className="bg-purple-100 text-purple-700 gap-1 px-3 py-1">
            <Camera className="w-3 h-3" />
            {photoCount}
          </Badge>
        )}
        
        {/* Due Date */}
        {task.due_date && (
          <Badge className={`gap-1 px-3 py-1 ${
            isOverdue ? "bg-red-100 text-red-700" :
            isDueToday ? "bg-orange-100 text-orange-700" :
            "bg-gray-100 text-gray-600"
          }`}>
            <Calendar className="w-3 h-3" />
            {format(parseISO(task.due_date), "dd.MM.")}
          </Badge>
        )}
        
        {/* Assignee */}
        {task.assigned_to_name && (
          <Badge className="bg-gray-100 text-gray-600 gap-1 px-3 py-1">
            <User className="w-3 h-3" />
            {task.assigned_to_name.split(" ")[0]}
          </Badge>
        )}
      </div>
    </div>
  );
}

// ============ TASK ROW COMPONENT (Desktop) ============
function TaskRow({ task, users, onEdit, onDelete, onStatusChange, onAddToReport }) {
  const statusConfig = STATUS_CONFIG[task.status] || STATUS_CONFIG.offen;
  const priorityConfig = PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG.normal;
  const StatusIcon = statusConfig.icon;
  
  const isOverdue = task.due_date && isPast(parseISO(task.due_date)) && task.status !== "erledigt";
  const isDueToday = task.due_date && isToday(parseISO(task.due_date));
  
  const completedChecklist = task.checklist?.filter(c => c.checked).length || 0;
  const totalChecklist = task.checklist?.length || 0;
  const photoCount = task.photo_count || task.photos?.length || 0;
  
  return (
    <tr className="hover:bg-gray-50 border-b border-gray-100 group">
      {/* Status Quick Toggle */}
      <td className="py-3 px-3 w-10">
        <button
          onClick={() => onStatusChange(task, task.status === "erledigt" ? "offen" : "erledigt")}
          className={`p-1 rounded-full transition-colors ${
            task.status === "erledigt" 
              ? "text-green-600 hover:bg-green-100" 
              : "text-gray-400 hover:text-green-600 hover:bg-green-50"
          }`}
        >
          {task.status === "erledigt" ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <Circle className="w-5 h-5" />
          )}
        </button>
      </td>
      
      {/* Task Number */}
      <td className="py-3 px-3 w-24">
        {task.task_number && (
          <span className="font-mono text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">
            {task.task_number}
          </span>
        )}
      </td>
      
      {/* Title & Description */}
      <td className="py-3 px-3">
        <div 
          className={`font-medium text-gray-900 cursor-pointer hover:text-blue-600 ${
            task.status === "erledigt" ? "line-through text-gray-400" : ""
          }`}
          onClick={() => onEdit(task)}
        >
          {task.title}
        </div>
        {task.area_location && (
          <p className="text-xs text-amber-600 flex items-center gap-1 mt-0.5">
            <MapPin className="w-3 h-3" />
            {task.area_location}
          </p>
        )}
        {task.task_location_marker?.plan_id && (
          <p className="text-xs text-blue-600 flex items-center gap-1 mt-0.5" data-testid={`task-row-marker-${task.id}`}>
            <MapPin className="w-3 h-3 fill-blue-600" />
            Standort auf Plan markiert
          </p>
        )}
        {task.description && (
          <p className="text-sm text-gray-500 line-clamp-1 mt-0.5">{task.description}</p>
        )}
        {totalChecklist > 0 && (
          <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
            <CheckSquare className="w-3 h-3" />
            {completedChecklist}/{totalChecklist}
          </div>
        )}
      </td>
      
      {/* Photo Count */}
      <td className="py-3 px-3 w-16 text-center">
        {photoCount > 0 ? (
          <Badge className="bg-purple-100 text-purple-700 gap-1">
            <Camera className="w-3 h-3" />
            {photoCount}
          </Badge>
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </td>
      
      {/* Status */}
      <td className="py-3 px-3">
        <Badge className={`${statusConfig.color} gap-1`}>
          <StatusIcon className="w-3 h-3" />
          {statusConfig.label}
        </Badge>
      </td>
      
      {/* Priority */}
      <td className="py-3 px-3">
        <Badge className={priorityConfig.color}>{priorityConfig.label}</Badge>
      </td>
      
      {/* Assigned To */}
      <td className="py-3 px-3">
        {(() => {
          const names = (task.assigned_users_names && task.assigned_users_names.length)
            ? task.assigned_users_names.filter(Boolean)
            : (task.assigned_to_name ? [task.assigned_to_name] : []);
          if (names.length === 0) return <span className="text-sm text-gray-400">-</span>;
          const first = names.slice(0, 2);
          const rest = names.length - first.length;
          return (
            <div className="flex flex-wrap gap-1" data-testid={`task-assignees-${task.id}`}>
              {first.map((n, i) => (
                <span key={i} className="inline-block px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700 border border-blue-100">{n}</span>
              ))}
              {rest > 0 && (
                <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 border border-gray-200">+{rest}</span>
              )}
            </div>
          );
        })()}
      </td>
      
      {/* Due Date */}
      <td className="py-3 px-3">
        {task.due_date ? (
          <span className={`text-sm ${
            isOverdue ? "text-red-600 font-medium" : 
            isDueToday ? "text-orange-600 font-medium" : 
            "text-gray-600"
          }`}>
            {isOverdue && <AlertTriangle className="w-3 h-3 inline mr-1" />}
            {format(parseISO(task.due_date), "dd.MM.yyyy")}
          </span>
        ) : (
          <span className="text-sm text-gray-400">-</span>
        )}
      </td>
      
      {/* Hours (Planned vs Logged) */}
      <td className="py-3 px-3">
        <div className="text-sm">
          <span className="text-gray-600">{task.logged_hours || 0}h</span>
          {task.planned_hours && (
            <span className="text-gray-400"> / {task.planned_hours}h</span>
          )}
        </div>
        {task.planned_hours > 0 && (
          <Progress 
            value={(task.logged_hours / task.planned_hours) * 100} 
            max={100}
            className="h-1 mt-1 w-16"
          />
        )}
      </td>
      
      {/* Actions */}
      <td className="py-3 px-3 text-right">
        <div className="flex items-center justify-end gap-1">
          {task.status === "erledigt" && (
            <button
              onClick={() => onAddToReport(task)}
              className="p-1.5 rounded hover:bg-blue-50 text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
              title="In Bautagesbericht übernehmen"
              data-testid={`task-to-report-${task.id}`}
            >
              <FileText className="w-4 h-4" />
            </button>
          )}
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1 rounded hover:bg-gray-200 opacity-0 group-hover:opacity-100 transition-opacity">
                <MoreVertical className="w-4 h-4 text-gray-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(task)}>
                <Edit className="w-4 h-4 mr-2" />
                Bearbeiten
              </DropdownMenuItem>
              {task.status === "erledigt" && (
                <DropdownMenuItem onClick={() => onAddToReport(task)} className="text-blue-600">
                  <FileText className="w-4 h-4 mr-2" />
                  In Bautagesbericht
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => onDelete(task)} className="text-red-600">
                <Trash2 className="w-4 h-4 mr-2" />
                Löschen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </td>
    </tr>
  );
}

// ============ MULTI-ASSIGNEE SELECT ============
function MultiAssigneeSelect({ users, selectedIds, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selectedUsers = users.filter((u) => selectedIds.includes(u.id));
  const filtered = users.filter((u) =>
    (u.name || u.email || "").toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (id) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter((x) => x !== id)
      : [...selectedIds, id];
    onChange(next);
  };

  const remove = (id, e) => {
    e?.stopPropagation();
    onChange(selectedIds.filter((x) => x !== id));
  };

  return (
    <div ref={containerRef} className="relative mt-1" data-testid="multi-assignee-select">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full min-h-[40px] px-3 py-1.5 text-left text-sm rounded-md border border-gray-300 dark:border-white/15 bg-white dark:bg-white/5 hover:border-gray-400 dark:hover:border-white/25 transition-colors"
      >
        {selectedUsers.length === 0 ? (
          <span className="text-gray-400">Nicht zugewiesen</span>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {selectedUsers.map((u) => (
              <span
                key={u.id}
                className="inline-flex items-center gap-1 rounded-full bg-blue-500/15 text-blue-700 dark:text-blue-200 border border-blue-500/30 px-2 py-0.5 text-xs font-medium"
                data-testid={`assignee-chip-${u.id}`}
              >
                {u.name}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => remove(u.id, e)}
                  className="ml-0.5 cursor-pointer hover:text-red-500 text-base leading-none"
                  data-testid={`assignee-chip-remove-${u.id}`}
                >
                  ×
                </span>
              </span>
            ))}
          </div>
        )}
      </button>
      {open && (
        <div className="absolute z-50 mt-1 left-0 right-0 max-h-72 overflow-auto rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-900 shadow-2xl">
          <div className="sticky top-0 p-2 bg-white dark:bg-slate-900 border-b border-gray-100 dark:border-white/10">
            <input
              type="text"
              placeholder="Suchen …"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              className="w-full px-2 py-1.5 text-sm rounded border border-gray-200 dark:border-white/15 bg-gray-50 dark:bg-white/5 focus:outline-none focus:border-blue-400"
              data-testid="multi-assignee-search"
            />
          </div>
          {filtered.length === 0 && (
            <div className="px-3 py-4 text-xs text-gray-400 text-center">Keine Treffer</div>
          )}
          {filtered.map((u) => {
            const checked = selectedIds.includes(u.id);
            return (
              <button
                key={u.id}
                type="button"
                onClick={() => toggle(u.id)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition-colors ${checked ? "bg-blue-50/60 dark:bg-blue-500/10" : ""}`}
                data-testid={`assignee-option-${u.id}`}
              >
                <input type="checkbox" readOnly checked={checked} className="w-4 h-4 accent-blue-500" />
                <span className="flex-1 truncate">{u.name}</span>
                {u.role && <span className="text-[10px] uppercase tracking-wider text-gray-400">{u.role}</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ============ MAIN COMPONENT ============
export default function TaskManagement() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDesktop = useIsDesktop();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState(null);
  
  // Filters
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [filterAssigned, setFilterAssigned] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    area_location: "",
    status: "offen",
    priority: "normal",
    assigned_to: "",
    assigned_users: [],
    due_date: "",
    planned_hours: "",
    labels: [],
    checklist: [],
    task_location_marker: null,
  });
  
  // Photos state for current task
  const [taskPhotos, setTaskPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // New checklist item
  const [newChecklistItem, setNewChecklistItem] = useState("");
  
  const isAdmin = user?.permissions?.can_approve_entries || user?.role === "admin";

  // Load data
  useEffect(() => {
    const loadData = async () => {
      if (!projectId) return;
      
      setLoading(true);
      try {
        const [projectRes, tasksRes, usersRes, summaryRes] = await Promise.all([
          authFetch(`${API}/projects`),
          authFetch(`${API}/projects/${projectId}/tasks`),
          authFetch(`${API}/users`),
          authFetch(`${API}/projects/${projectId}/tasks-summary`),
        ]);
        
        if (projectRes.ok) {
          const projects = await projectRes.json();
          const proj = projects.find(p => p.id === projectId);
          setProject(proj);
        }
        
        if (tasksRes.ok) {
          const tasksData = await tasksRes.json();
          setTasks(tasksData);
        }
        
        if (usersRes.ok) {
          const usersData = await usersRes.json();
          setUsers(usersData.filter(u => u.is_active !== false));
        }
        
        if (summaryRes.ok) {
          const summaryData = await summaryRes.json();
          setSummary(summaryData);
        }
      } catch (error) {
        toast.error("Fehler beim Laden");
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [projectId]);

  // Filter tasks
  const filteredTasks = tasks.filter(task => {
    if (filterStatus && task.status !== filterStatus) return false;
    if (filterPriority && task.priority !== filterPriority) return false;
    if (filterAssigned && task.assigned_to !== filterAssigned) return false;
    if (searchQuery && !task.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  // Sort: priority (kritisch first), then due date
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    const priorityOrder = { kritisch: 0, hoch: 1, normal: 2, niedrig: 3 };
    const priorityDiff = (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2);
    if (priorityDiff !== 0) return priorityDiff;
    
    if (a.due_date && b.due_date) return a.due_date.localeCompare(b.due_date);
    if (a.due_date) return -1;
    if (b.due_date) return 1;
    
    return 0;
  });

  // Handlers
  const handleOpenDialog = async (task = null) => {
    if (task) {
      setSelectedTask(task);
      const auArr = Array.isArray(task.assigned_users) && task.assigned_users.length
        ? task.assigned_users
        : (task.assigned_to ? [task.assigned_to] : []);
      setFormData({
        title: task.title_original || task.title,
        description: task.description_original || task.description || "",
        area_location: task.area_location_original || task.area_location || "",
        status: task.status,
        priority: task.priority,
        assigned_to: task.assigned_to || "",
        assigned_users: auArr,
        due_date: task.due_date || "",
        planned_hours: task.planned_hours || "",
        labels: task.labels || [],
        checklist: task.checklist || [],
        task_location_marker: task.task_location_marker || null,
      });
      setTaskPhotos(task.photos || []);
    } else {
      setSelectedTask(null);
      setFormData({
        title: "",
        description: "",
        area_location: "",
        status: "offen",
        priority: "normal",
        assigned_to: "",
        assigned_users: [],
        due_date: "",
        planned_hours: "",
        labels: [],
        checklist: [],
        task_location_marker: null,
      });
      setTaskPhotos([]);
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error("Titel ist erforderlich");
      return;
    }
    
    try {
      const payload = {
        ...formData,
        planned_hours: formData.planned_hours ? parseFloat(formData.planned_hours) : null,
        assigned_to: formData.assigned_users?.length ? formData.assigned_users[0] : (formData.assigned_to || null),
        assigned_users: formData.assigned_users || [],
        due_date: formData.due_date || null,
      };
      
      let res;
      if (selectedTask) {
        res = await authFetch(`${API}/projects/${projectId}/tasks/${selectedTask.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
        // Marker explizit löschen, wenn er vorhanden war und jetzt entfernt wurde
        if (res.ok && selectedTask.task_location_marker && !formData.task_location_marker) {
          await authFetch(`${API}/projects/${projectId}/tasks/${selectedTask.id}/marker`, { method: "DELETE" });
        }
      } else {
        res = await authFetch(`${API}/projects/${projectId}/tasks`, {
          method: "POST",
          body: JSON.stringify({ ...payload, project_id: projectId }),
        });
      }
      
      if (res.ok) {
        toast.success(selectedTask ? "Aufgabe aktualisiert" : "Aufgabe erstellt");
        setDialogOpen(false);
        // Reload tasks
        const tasksRes = await authFetch(`${API}/projects/${projectId}/tasks`);
        if (tasksRes.ok) setTasks(await tasksRes.json());
        const summaryRes = await authFetch(`${API}/projects/${projectId}/tasks-summary`);
        if (summaryRes.ok) setSummary(await summaryRes.json());
      } else {
        const err = await res.json();
        toast.error(err.detail || "Fehler beim Speichern");
      }
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async () => {
    if (!selectedTask) return;
    
    try {
      const res = await authFetch(`${API}/projects/${projectId}/tasks/${selectedTask.id}`, {
        method: "DELETE",
      });
      
      if (res.ok) {
        toast.success("Aufgabe gelöscht");
        setDeleteDialogOpen(false);
        setDialogOpen(false);
        setTasks(prev => prev.filter(t => t.id !== selectedTask.id));
        const summaryRes = await authFetch(`${API}/projects/${projectId}/tasks-summary`);
        if (summaryRes.ok) setSummary(await summaryRes.json());
      }
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      const res = await authFetch(`${API}/projects/${projectId}/tasks/${task.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
      });
      
      if (res.ok) {
        setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
        toast.success(newStatus === "erledigt" ? "Aufgabe erledigt" : "Status geändert");
        const summaryRes = await authFetch(`${API}/projects/${projectId}/tasks-summary`);
        if (summaryRes.ok) setSummary(await summaryRes.json());
      }
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleAddToReport = async (task) => {
    try {
      const res = await authFetch(`${API}/projects/${projectId}/tasks/${task.id}/to-daily-report`, {
        method: "POST",
        body: JSON.stringify({ task_id: task.id }),
      });
      
      if (res.ok) {
        const result = await res.json();
        toast.success(result.message || "Aufgabe zum Bautagesbericht hinzugefügt");
        navigate(`/app/projekte/${projectId}/berichte`);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Fehler beim Übernehmen");
      }
    } catch (error) {
      toast.error("Fehler beim Übernehmen in Bautagesbericht");
    }
  };

  // Photo handlers
  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !selectedTask) return;
    
    const remainingSlots = MAX_PHOTOS - taskPhotos.length;
    if (remainingSlots <= 0) {
      toast.error(`Maximal ${MAX_PHOTOS} Fotos pro Aufgabe erlaubt`);
      return;
    }
    
    const filesToUpload = files.slice(0, remainingSlots);
    
    setUploadingPhoto(true);
    let uploaded = 0;
    
    for (const file of filesToUpload) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} ist zu groß (max. 10 MB)`);
        continue;
      }
      
      const formData = new FormData();
      formData.append("file", file);
      
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API}/projects/${projectId}/tasks/${selectedTask.id}/photos`, {
          method: "POST",
          headers: { "Authorization": `Bearer ${token}` },
          body: formData,
        });
        
        if (res.ok) {
          const data = await res.json();
          setTaskPhotos(prev => [...prev, data.photo]);
          uploaded++;
        } else {
          const err = await res.json();
          toast.error(err.detail || `Fehler bei ${file.name}`);
        }
      } catch (error) {
        toast.error(`Fehler beim Hochladen von ${file.name}`);
      }
    }
    
    if (uploaded > 0) {
      toast.success(`${uploaded} Foto${uploaded > 1 ? "s" : ""} hochgeladen`);
      // Refresh tasks to update photo count
      const tasksRes = await authFetch(`${API}/projects/${projectId}/tasks`);
      if (tasksRes.ok) setTasks(await tasksRes.json());
    }
    
    setUploadingPhoto(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handlePhotoDelete = async (photo) => {
    if (!selectedTask) return;
    
    try {
      const res = await authFetch(`${API}/projects/${projectId}/tasks/${selectedTask.id}/photos/${photo.id}`, {
        method: "DELETE",
      });
      
      if (res.ok) {
        setTaskPhotos(prev => prev.filter(p => p.id !== photo.id));
        toast.success("Foto gelöscht");
        // Refresh tasks
        const tasksRes = await authFetch(`${API}/projects/${projectId}/tasks`);
        if (tasksRes.ok) setTasks(await tasksRes.json());
      }
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const addChecklistItem = () => {
    if (!newChecklistItem.trim()) return;
    setFormData(prev => ({
      ...prev,
      checklist: [...prev.checklist, { id: Date.now().toString(), text: newChecklistItem, checked: false }],
    }));
    setNewChecklistItem("");
  };

  const toggleChecklistItem = (itemId) => {
    setFormData(prev => ({
      ...prev,
      checklist: prev.checklist.map(item => 
        item.id === itemId ? { ...item, checked: !item.checked } : item
      ),
    }));
  };

  const removeChecklistItem = (itemId) => {
    setFormData(prev => ({
      ...prev,
      checklist: prev.checklist.filter(item => item.id !== itemId),
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <AlertTriangle className="w-12 h-12 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Projekt nicht gefunden</h2>
        <Button variant="outline" onClick={() => navigate("/projects")}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>
      </div>
    );
  }

  return (
    <div className={`${isDesktop ? 'space-y-6' : 'pb-24'}`}>
      {isDesktop && <ProjectTopNav projectId={projectId} />}
      {/* ==================== MOBILE VIEW ==================== */}
      {!isDesktop && (
        <>
          {/* Mobile Header */}
          <div className="sticky top-0 bg-gray-50 z-10 px-4 py-3 border-b">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold text-gray-900">Aufgaben</h1>
                <p className="text-sm text-gray-500">{project.name}</p>
              </div>
              <button
                onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
                className={`p-2 rounded-lg ${mobileFilterOpen ? 'bg-blue-100 text-blue-600' : 'bg-gray-100'}`}
              >
                <Filter className="w-5 h-5" />
              </button>
            </div>
            
            {mobileFilterOpen && (
              <div className="mt-3 pt-3 border-t space-y-3">
                <Input
                  placeholder="Suchen..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-10"
                />
                <div className="flex gap-2 overflow-x-auto pb-2">
                  <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                    <SelectTrigger className="h-9 w-auto min-w-[100px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle</SelectItem>
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={filterPriority || "all"} onValueChange={(v) => setFilterPriority(v === "all" ? "" : v)}>
                    <SelectTrigger className="h-9 w-auto min-w-[100px]">
                      <SelectValue placeholder="Priorität" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Alle</SelectItem>
                      {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
          
          {/* Mobile Summary */}
          {summary && (
            <div className="px-4 py-3 bg-white border-b">
              <div className="flex justify-between text-center">
                <div>
                  <div className="text-lg font-bold">{summary.total}</div>
                  <div className="text-xs text-gray-500">Gesamt</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-blue-600">{summary.by_status.in_arbeit || 0}</div>
                  <div className="text-xs text-gray-500">In Arbeit</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-green-600">{summary.by_status.erledigt || 0}</div>
                  <div className="text-xs text-gray-500">Erledigt</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-red-600">{summary.overdue || 0}</div>
                  <div className="text-xs text-gray-500">Überfällig</div>
                </div>
              </div>
              {summary.total > 0 && (
                <Progress value={summary.progress_percent || 0} className="h-1.5 mt-3" />
              )}
            </div>
          )}
          
          {/* Mobile Task List */}
          <div className="px-4 py-3">
            {sortedTasks.length > 0 ? (
              sortedTasks.map((task) => (
                <MobileTaskCard
                  key={task.id}
                  task={task}
                  onEdit={handleOpenDialog}
                  onDelete={(t) => { setSelectedTask(t); setDeleteDialogOpen(true); }}
                  onStatusChange={handleStatusChange}
                  onAddToReport={handleAddToReport}
                />
              ))
            ) : (
              <div className="text-center py-12 text-gray-500">
                <ListTodo className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Keine Aufgaben gefunden</p>
              </div>
            )}
          </div>
          
          {/* Mobile FAB */}
          {isAdmin && (
            <div className="fixed bottom-6 right-6 z-50">
              <Button 
                onClick={() => handleOpenDialog()} 
                size="lg"
                className="rounded-full w-14 h-14 shadow-lg"
                data-testid="mobile-add-task-btn"
              >
                <Plus className="w-6 h-6" />
              </Button>
            </div>
          )}
        </>
      )}

      {/* ==================== DESKTOP VIEW ==================== */}
      {isDesktop && (
        <>
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate(`/app/projekte/${projectId}/dashboard`)}
                className="mt-1"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: project.color || "#6B7280" }} />
                  <h1 className="text-2xl font-bold text-gray-900 font-[Manrope]">Aufgaben</h1>
                </div>
                <p className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                  <ListTodo className="w-4 h-4" />
                  {project.name} · {tasks.length} Aufgaben
                </p>
              </div>
            </div>
            
            {isAdmin && (
              <Button onClick={() => handleOpenDialog()} data-testid="add-task-btn">
                <Plus className="w-4 h-4 mr-2" />
                Neue Aufgabe
              </Button>
            )}
          </div>

          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-5 gap-4">
              <div className="bg-white border rounded-lg p-4">
                <div className="text-2xl font-bold">{summary.total}</div>
                <div className="text-sm text-gray-500">Gesamt</div>
              </div>
              <div className="bg-white border rounded-lg p-4">
                <div className="text-2xl font-bold text-blue-600">{summary.by_status.in_arbeit}</div>
                <div className="text-sm text-gray-500">In Arbeit</div>
              </div>
              <div className="bg-white border rounded-lg p-4">
                <div className="text-2xl font-bold text-green-600">{summary.by_status.erledigt}</div>
                <div className="text-sm text-gray-500">Erledigt</div>
                {summary.total > 0 && (
                  <Progress value={summary.progress_percent} className="h-1 mt-2" />
                )}
              </div>
              <div className="bg-white border rounded-lg p-4">
                <div className="text-2xl font-bold text-red-600">{summary.overdue}</div>
                <div className="text-sm text-gray-500">Überfällig</div>
              </div>
              <div className="bg-white border rounded-lg p-4">
                <div className="text-2xl font-bold text-orange-600">{summary.open_critical}</div>
                <div className="text-sm text-gray-500">Kritisch offen</div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="bg-white border rounded-lg p-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                <Search className="w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Aufgabe suchen..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-9"
                />
              </div>
              
              <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[140px] h-9">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Status</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterPriority || "all"} onValueChange={(v) => setFilterPriority(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[140px] h-9">
                  <SelectValue placeholder="Priorität" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Prioritäten</SelectItem>
                  {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterAssigned || "all"} onValueChange={(v) => setFilterAssigned(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[160px] h-9">
                  <SelectValue placeholder="Mitarbeiter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Mitarbeiter</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Task Table */}
          <div className="bg-white border rounded-lg overflow-hidden">
            {sortedTasks.length > 0 ? (
              <table className="w-full">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="py-3 px-3 w-10"></th>
                    <th className="py-3 px-3 text-left w-24">Nr.</th>
                    <th className="py-3 px-3 text-left">Aufgabe</th>
                    <th className="py-3 px-3 text-center w-16">📷</th>
                    <th className="py-3 px-3 text-left">Status</th>
                    <th className="py-3 px-3 text-left">Priorität</th>
                    <th className="py-3 px-3 text-left">Zugewiesen</th>
                    <th className="py-3 px-3 text-left">Fällig</th>
                    <th className="py-3 px-3 text-left">Stunden</th>
                    <th className="py-3 px-3 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTasks.map((task) => (
                    <TaskRow
                      key={task.id}
                      task={task}
                      users={users}
                      onEdit={handleOpenDialog}
                      onDelete={(t) => { setSelectedTask(t); setDeleteDialogOpen(true); }}
                      onStatusChange={handleStatusChange}
                      onAddToReport={handleAddToReport}
                    />
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <ListTodo className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Keine Aufgaben gefunden</p>
                {isAdmin && (
                  <Button variant="outline" className="mt-4" onClick={() => handleOpenDialog()}>
                    <Plus className="w-4 h-4 mr-2" />
                    Erste Aufgabe erstellen
                  </Button>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Task Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {selectedTask ? "Aufgabe bearbeiten" : "Neue Aufgabe"}
              {selectedTask?.task_number && (
                <Badge variant="outline" className="font-mono">
                  {selectedTask.task_number}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedTask ? "Ändern Sie die Details der Aufgabe" : "Erstellen Sie eine neue Aufgabe für dieses Projekt"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Titel *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Aufgabentitel"
                className="mt-1"
              />
            </div>
            
            <div>
              <Label>Beschreibung</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Detaillierte Beschreibung..."
                className="mt-1"
                rows={3}
              />
            </div>

            <div>
              <Label>Bereich / Ort im Projekt (optional)</Label>
              <Input
                value={formData.area_location || ""}
                onChange={(e) => setFormData({ ...formData, area_location: e.target.value })}
                placeholder="z. B. Hinterer Technikbereich, Halle 2"
                className="mt-1"
                data-testid="task-area-location-input"
              />
            </div>

            <div>
              <Label>Standort auf Plan (optional)</Label>
              <div className="mt-1">
                <TaskMarkerPicker
                  projectId={projectId}
                  marker={formData.task_location_marker}
                  onChange={(m) => setFormData({ ...formData, task_location_marker: m })}
                  theme="light"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Priorität</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Zuweisen an</Label>
                <MultiAssigneeSelect
                  users={users}
                  selectedIds={formData.assigned_users || []}
                  onChange={(ids) => setFormData({ ...formData, assigned_users: ids })}
                />
              </div>
              
              <div>
                <Label>Fälligkeitsdatum</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div>
              <Label>Geplante Stunden</Label>
              <Input
                type="number"
                min="0"
                step="0.5"
                value={formData.planned_hours}
                onChange={(e) => setFormData({ ...formData, planned_hours: e.target.value })}
                placeholder="z.B. 8"
                className="mt-1 w-32"
              />
            </div>
            
            {/* Soll/Ist for existing tasks */}
            {selectedTask && selectedTask.logged_hours > 0 && (
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4" />
                  Zeiterfassung (nur lesen)
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500">Geplant</div>
                    <div className="font-semibold">{selectedTask.planned_hours || 0}h</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Erfasst</div>
                    <div className="font-semibold">{selectedTask.logged_hours}h</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Delta</div>
                    <div className="font-semibold">
                      {selectedTask.planned_hours 
                        ? `${(selectedTask.logged_hours - selectedTask.planned_hours) > 0 ? "+" : ""}${(selectedTask.logged_hours - selectedTask.planned_hours).toFixed(1)}h`
                        : "-"
                      }
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Photos Section - Only for existing tasks */}
            {selectedTask && (
              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <Label className="flex items-center gap-2">
                    <Camera className="w-4 h-4" />
                    Fotos zur Aufgabe
                    <span className="text-gray-500 font-normal">({taskPhotos.length} / {MAX_PHOTOS})</span>
                  </Label>
                  
                  {taskPhotos.length < MAX_PHOTOS && (
                    <div className="flex gap-2">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handlePhotoUpload}
                        accept="image/jpeg,image/png,image/heic,image/heif"
                        multiple
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingPhoto}
                      >
                        {uploadingPhoto ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 mr-2" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        Foto hochladen
                      </Button>
                    </div>
                  )}
                </div>
                
                {taskPhotos.length > 0 ? (
                  <PhotoGallery
                    photos={taskPhotos}
                    onDelete={handlePhotoDelete}
                    onPreview={setPreviewPhoto}
                    isEditable={true}
                  />
                ) : (
                  <div className="text-center py-6 text-gray-400 border-2 border-dashed rounded-lg">
                    <Image className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Noch keine Fotos</p>
                    <p className="text-xs">Klicken Sie auf "Foto hochladen" um Bilder hinzuzufügen</p>
                  </div>
                )}
                
                {taskPhotos.length >= MAX_PHOTOS && (
                  <p className="text-xs text-orange-600 mt-2">
                    Maximale Anzahl Fotos erreicht ({MAX_PHOTOS})
                  </p>
                )}
              </div>
            )}
            
            {/* Note for new tasks */}
            {!selectedTask && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                <Camera className="w-4 h-4 inline mr-2" />
                Fotos können nach dem Erstellen der Aufgabe hinzugefügt werden.
              </div>
            )}
            
            {/* Checklist */}
            <div>
              <Label>Checkliste</Label>
              <div className="mt-2 space-y-2">
                {formData.checklist.map((item) => (
                  <div key={item.id} className="flex items-center gap-2 group">
                    <button
                      type="button"
                      onClick={() => toggleChecklistItem(item.id)}
                      className={item.checked ? "text-green-600" : "text-gray-400"}
                    >
                      {item.checked ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                    </button>
                    <span className={`flex-1 text-sm ${item.checked ? "line-through text-gray-400" : ""}`}>
                      {item.text}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeChecklistItem(item.id)}
                      className="text-red-500 opacity-0 group-hover:opacity-100"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <Input
                    value={newChecklistItem}
                    onChange={(e) => setNewChecklistItem(e.target.value)}
                    placeholder="Neuer Punkt..."
                    className="h-8 text-sm"
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addChecklistItem())}
                  />
                  <Button type="button" variant="outline" size="sm" onClick={addChecklistItem}>
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-4 border-t">
            {selectedTask ? (
              <Button
                variant="outline"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => setDeleteDialogOpen(true)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Löschen
              </Button>
            ) : (
              <div />
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button onClick={handleSave}>
                <CheckCircle className="w-4 h-4 mr-2" />
                {selectedTask ? "Speichern" : "Erstellen"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Photo Preview Modal */}
      <PhotoPreviewModal photo={previewPhoto} onClose={() => setPreviewPhoto(null)} />

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Aufgabe löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Diese Aktion kann nicht rückgängig gemacht werden. Die Aufgabe "{selectedTask?.title}" wird dauerhaft gelöscht.
              {(selectedTask?.photo_count > 0 || selectedTask?.photos?.length > 0) && (
                <span className="block mt-2 text-orange-600">
                  ⚠️ {selectedTask?.photo_count || selectedTask?.photos?.length} Foto(s) werden ebenfalls gelöscht.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
